import { useCallback, useEffect, useState } from "react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import type { PolicyAssignmentRow } from "../../../lib/policySigning.js";
import { SignedCopyDownload } from "../../PolicySignPanel.js";
import { Badge } from "../../ui/badge.js";
import { Button } from "../../ui/button.js";
import { Checkbox } from "../../ui/checkbox.js";

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// HR's "Documents to sign" list for one employee (profile → Policy Documents,
// and the Onboarding card): every policy document with a published version.
// Ticked documents are the ones HR picked: a new hire signs exactly these in
// onboarding step 8, and can't submit (nor be activated) until they are
// signed. A document every employee signs is signed in the login pop-up
// anyway, ticked or not; ticking it also puts it in step 8. Each row shows its
// state, the latest signed copy, "Ask to sign again", and the earlier copies.
export function PolicyAssignmentsPanel({ employeeId, readOnly, onChanged }: {
  employeeId: string;
  /** Shown but not editable (completed onboarding, or a view-only profile). */
  readOnly: boolean;
  /** After a change, so the parent can refresh what depends on it. */
  onChanged?: () => void;
}) {
  const [rows, setRows] = useState<PolicyAssignmentRow[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get<PolicyAssignmentRow[]>(`/employees/${employeeId}/policy-assignments`)
      .then(r => setRows(r.data))
      .catch((err) => { logError("PolicyAssignmentsPanel.load", err, { employeeId }); setError(errMsg(err) ?? "Could not load the documents."); });
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  async function change(kind: string, request: () => Promise<{ data: PolicyAssignmentRow[] }>) {
    setSaving(true); setError("");
    try {
      setRows((await request()).data);
      onChanged?.();
    } catch (err) {
      logError("PolicyAssignmentsPanel.change", err, { employeeId, kind });
      setError(errMsg(err) ?? "Could not update the documents to sign.");
    } finally {
      setSaving(false);
    }
  }

  function toggle(kind: string, assigned: boolean) {
    if (!rows) return;
    const kinds = rows.filter(r => (r.kind === kind ? assigned : r.assigned)).map(r => r.kind);
    void change(kind, () => api.put<PolicyAssignmentRow[]>(`/employees/${employeeId}/policy-assignments`, { kinds }));
  }

  function askAgain(kind: string) {
    void change(kind, () => api.post<PolicyAssignmentRow[]>(`/employees/${employeeId}/policy-assignments/${encodeURIComponent(kind)}/request`, {}));
  }

  return (
    <div className="border-b border-surface-100 py-2.5">
      <div className="mb-1.5 text-[13px] font-medium">Documents to sign</div>
      {error && <p className="mb-1.5 text-xs text-[var(--color-error-fg)]">{error}</p>}
      {rows === null ? (
        <p className="text-xs text-surface-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-surface-500">No policy document is published yet. Add one in Admin → Policy Documents.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map(row => {
            const id = `policy-assign-${row.kind}`;
            const earlier = row.signatures.slice(1);
            return (
              <li key={row.kind} className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={row.assigned}
                    disabled={readOnly || saving || !row.current}
                    onCheckedChange={(v) => toggle(row.kind, v === true)}
                  />
                  <label htmlFor={id} className="min-w-0 flex-1 text-xs text-surface-700">
                    {row.typeName}
                    {row.current && <span className="text-surface-400"> · {row.current.title} v{row.current.version}</span>}
                    {row.allEmployees && <span className="text-surface-400"> · all employees sign it at login, yearly</span>}
                  </label>
                  {row.due ? (
                    <Badge tone="warning">Pending</Badge>
                  ) : row.signature ? (
                    <Badge tone="success">Signed {fmtDate(row.signature.signedAt)}</Badge>
                  ) : (
                    <Badge tone="neutral">Not required</Badge>
                  )}
                  {row.signature?.downloadUrl && <SignedCopyDownload url={row.signature.downloadUrl} title={row.signature.title} />}
                  {!readOnly && !row.due && !row.allEmployees && row.signature && row.current && (
                    <Button size="xs" variant="outline" disabled={saving} onClick={() => askAgain(row.kind)}
                      title="The employee is asked to sign the current version at their next login">
                      Ask to sign again
                    </Button>
                  )}
                </div>
                {earlier.length > 0 && (
                  <ul className="ml-6 space-y-1">
                    {earlier.map(s => (
                      <li key={`${s.kind}-${s.signedAt}`} className="flex flex-wrap items-center gap-2 text-[11px] text-surface-500">
                        <span className="min-w-0 flex-1">Earlier: {s.title}{s.version && ` v${s.version}`} · signed {fmtDate(s.signedAt)}</span>
                        {s.downloadUrl && <SignedCopyDownload url={s.downloadUrl} title={s.title} />}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
